/*
 * Fabric reference popover.
 *
 * On /pages/our-silks each fabric card can open a popover showing the garments
 * we have made from that fabric. The fabric -> product-handle map lives in
 * assets/fabric-reference-map.json; the photos and titles are fetched from
 * /products/<handle>.js when the popover opens, so they always match what is
 * live on the product page.
 *
 * Turned on per section by the "Open a fabric reference popover" checkbox in
 * the theme editor (sections/multicolumn.liquid).
 */
(function () {
	'use strict';

	// Several multicolumn sections on the same page each load this file.
	if (window.fabricReferenceLoaded) return;
	window.fabricReferenceLoaded = true;

	var MAX_PHOTOS_PER_PIECE = 3;
	var DRAG_TOLERANCE = 8;
	var EMPTY_TEXT = 'We are photographing pieces in this fabric right now — please check back soon.';
	var CLOSE_LABEL = 'Close';

	function normalize(name) {
		return (name || '')
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, ' ')
			.trim();
	}

	function escapeHtml(value) {
		return String(value == null ? '' : value)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}

	function rootUrl() {
		var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
		return root || '/';
	}

	function sizedImage(url, width) {
		if (!url) return '';
		return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
	}

	var productCache = {};

	function fetchProduct(handle) {
		if (!productCache[handle]) {
			productCache[handle] = fetch(rootUrl() + 'products/' + handle + '.js')
				.then(function (response) {
					// A product that was deleted or unpublished simply drops out
					// of the popover rather than breaking it.
					return response.ok ? response.json() : null;
				})
				.catch(function () {
					return null;
				});
		}

		return productCache[handle];
	}

	function productUrl(product) {
		return product.url || rootUrl() + 'products/' + product.handle;
	}

	function shortTitle(title) {
		// Product titles are long SEO strings — everything up to the first
		// comma reads as the name of the piece.
		return String(title || '').split(',')[0].trim() || String(title || '');
	}

	function nameMarkup(url, title) {
		return (
			'<a class="fabric-reference-tile__name" href="' +
			escapeHtml(url) +
			'" title="' +
			escapeHtml(title) +
			'">' +
			escapeHtml(shortTitle(title)) +
			'</a>'
		);
	}

	function tileMarkup(url, image, title) {
		return (
			'<figure class="fabric-reference-tile">' +
			'<a class="fabric-reference-tile__photo" href="' +
			escapeHtml(url) +
			'"' +
			(title ? '' : ' tabindex="-1" aria-hidden="true"') +
			'>' +
			'<img src="' +
			escapeHtml(sizedImage(image, 600)) +
			'" alt="" loading="lazy" width="600" height="800">' +
			'</a>' +
			(title ? '<figcaption>' + nameMarkup(url, title) + '</figcaption>' : '') +
			'</figure>'
		);
	}

	function piecesMarkup(products) {
		// One garment: show a few angles of it. Several garments: one photo
		// each, so the fabric reads as a row of finished pieces.
		if (products.length === 1) {
			var product = products[0];
			var url = productUrl(product);
			var photos = (product.images || [])
				.slice(0, MAX_PHOTOS_PER_PIECE)
				.map(function (image) {
					return tileMarkup(url, image, null);
				})
				.join('');

			return (
				'<div class="fabric-reference-grid fabric-reference-grid--single">' +
				photos +
				'</div>' +
				'<p class="fabric-reference-caption">' +
				nameMarkup(url, product.title) +
				'</p>'
			);
		}

		return (
			'<div class="fabric-reference-grid">' +
			products
				.map(function (product) {
					return tileMarkup(
						productUrl(product),
						product.featured_image || (product.images || [])[0],
						product.title
					);
				})
				.join('') +
			'</div>'
		);
	}

	function buildModal() {
		var element = document.createElement('div');
		element.className = 'fabric-reference-modal';
		element.innerHTML =
			'<button type="button" class="fabric-reference-modal__overlay" data-overlay aria-label="' +
			CLOSE_LABEL +
			'" tabindex="-1"></button>' +
			'<div class="fabric-reference-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="FabricReferenceTitle">' +
			'<div class="fabric-reference-modal__header">' +
			'<div>' +
			'<p class="fabric-reference-modal__subtitle subtitle" data-subtitle></p>' +
			'<h2 id="FabricReferenceTitle" class="fabric-reference-modal__title h3" data-title></h2>' +
			'</div>' +
			'<button type="button" class="fabric-reference-modal__close" data-close aria-label="' +
			CLOSE_LABEL +
			'">' +
			'<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
			'<path d="M15.625 4.375L4.375 15.625" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>' +
			'<path d="M15.625 15.625L4.375 4.375" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>' +
			'</svg>' +
			'</button>' +
			'</div>' +
			'<div class="fabric-reference-modal__body" data-body></div>' +
			'</div>';

		document.body.appendChild(element);
		return element;
	}

	function Modal() {
		this.element = buildModal();
		this.dialog = this.element.querySelector('.fabric-reference-modal__dialog');
		this.titleNode = this.element.querySelector('[data-title]');
		this.subtitleNode = this.element.querySelector('[data-subtitle]');
		this.bodyNode = this.element.querySelector('[data-body]');
		this.closeButton = this.element.querySelector('[data-close]');
		this.lastFocused = null;
		this.requestId = 0;

		var close = this.close.bind(this);
		this.closeButton.addEventListener('click', close);
		this.element.querySelector('[data-overlay]').addEventListener('click', close);

		this.element.addEventListener(
			'keydown',
			function (event) {
				if (event.key === 'Escape') {
					close();
				} else if (event.key === 'Tab') {
					this.trapFocus(event);
				}
			}.bind(this)
		);
	}

	Modal.prototype.trapFocus = function (event) {
		var focusable = this.dialog.querySelectorAll('a[href], button:not([disabled])');
		if (!focusable.length) return;

		var first = focusable[0];
		var last = focusable[focusable.length - 1];

		if (event.shiftKey && document.activeElement === first) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && document.activeElement === last) {
			event.preventDefault();
			first.focus();
		}
	};

	Modal.prototype.open = function (card) {
		var handles = (card.getAttribute('data-fabric-handles') || '').split(',').filter(Boolean);
		var subheading = card.getAttribute('data-fabric-name-zh') || '';

		this.lastFocused = document.activeElement;
		this.titleNode.textContent = card.getAttribute('data-fabric-name') || '';
		this.subtitleNode.textContent = subheading;
		this.subtitleNode.hidden = subheading === '';
		this.bodyNode.innerHTML =
			'<div class="fabric-reference-skeleton">' +
			new Array(handles.length + 1).join('<span></span>') +
			'</div>';

		this.element.classList.add('is-open');
		document.documentElement.classList.add('fabric-reference-open');
		document.body.classList.add('fabric-reference-open');
		this.closeButton.focus();

		var requestId = ++this.requestId;
		var self = this;

		Promise.all(handles.map(fetchProduct)).then(function (products) {
			// The shopper may have closed this popover, or opened another one,
			// while the products were loading — only paint the newest request.
			if (requestId !== self.requestId) return;

			var found = products.filter(Boolean);
			self.bodyNode.innerHTML = found.length
				? piecesMarkup(found)
				: '<p class="fabric-reference-modal__empty">' + escapeHtml(EMPTY_TEXT) + '</p>';
		});
	};

	Modal.prototype.close = function () {
		this.requestId++;
		this.element.classList.remove('is-open');
		document.documentElement.classList.remove('fabric-reference-open');
		document.body.classList.remove('fabric-reference-open');
		this.bodyNode.innerHTML = '';

		if (this.lastFocused && this.lastFocused.focus) {
			this.lastFocused.focus();
		}
	};

	function activateCard(card, handles, modal) {
		if (card.classList.contains('fabric-reference-card')) return;

		card.setAttribute('data-fabric-handles', handles.join(','));
		card.classList.add('fabric-reference-card');

		var trigger = card.querySelector('.multicolumn__link-overlay');
		if (trigger) {
			trigger.setAttribute('role', 'button');
			trigger.setAttribute('aria-haspopup', 'dialog');
			trigger.removeAttribute('href');
			trigger.setAttribute('tabindex', '0');
		}

		// The "More Details" button is marked disabled while its link is empty
		// in the theme editor — it does something now.
		var label = card.querySelector('.multicolumn-card__link');
		if (label) {
			label.removeAttribute('aria-disabled');
			label.setAttribute('aria-haspopup', 'dialog');
		}

		var startX = 0;
		var startY = 0;

		card.addEventListener('pointerdown', function (event) {
			startX = event.clientX;
			startY = event.clientY;
		});

		card.addEventListener('click', function (event) {
			// Cards inside a slider fire a click at the end of a drag.
			if (
				Math.abs(event.clientX - startX) > DRAG_TOLERANCE ||
				Math.abs(event.clientY - startY) > DRAG_TOLERANCE
			) {
				return;
			}

			event.preventDefault();
			modal.open(card);
		});

		card.addEventListener('keydown', function (event) {
			if (event.key !== 'Enter' && event.key !== ' ') return;
			if (!event.target.classList.contains('multicolumn__link-overlay')) return;

			event.preventDefault();
			modal.open(card);
		});
	}

	var modal = null;

	function eachCard(selector, callback) {
		Array.prototype.forEach.call(
			document.querySelectorAll('[data-fabric-reference] ' + selector),
			callback
		);
	}

	/* Garments picked by hand in the theme editor. */
	function activatePickedCards() {
		eachCard('[data-fabric-handles]', function (card) {
			var handles = card.getAttribute('data-fabric-handles').split(',').filter(Boolean);
			if (!handles.length) return;

			modal = modal || new Modal();
			activateCard(card, handles, modal);
		});
	}

	/* Everything else falls back to the matches from Mud Silk Lab. */
	function activateMappedCards(fabrics) {
		eachCard('[data-fabric-name]', function (card) {
			var handles = fabrics[normalize(card.getAttribute('data-fabric-name'))];
			// Fabrics with no garment photos yet stay exactly as they are
			// today rather than opening an empty popover.
			if (!handles || !handles.length) return;

			modal = modal || new Modal();
			activateCard(card, handles, modal);
		});
	}

	function init() {
		var section = document.querySelector('[data-fabric-reference]');
		if (!section) return;

		activatePickedCards();

		fetch(section.getAttribute('data-fabric-reference'))
			.then(function (response) {
				return response.ok ? response.json() : null;
			})
			.then(function (map) {
				if (map && map.fabrics) activateMappedCards(map.fabrics);
			})
			.catch(function (error) {
				console.warn('[fabric-reference] could not load the fabric map', error);
			});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}

	// Re-run when a section is re-rendered in the theme editor.
	document.addEventListener('shopify:section:load', init);
})();
